// Utilidades COMPARTIDAS del módulo Informes: periodos, exportación a CSV y generación del PDF.
//
// Viven fuera de la página porque hay DOS consumidores —el informe suelto (Informes.jsx) y los
// informes COMBINADOS (InformesCombinados.jsx)— y un segundo generador de PDF acabaría pareciéndose
// cada vez menos al primero: mismo papel, dos cabeceras distintas. Aquí hay UN generador que sabe
// apilar N tablas; con una sola tabla emite exactamente el mismo documento de siempre.

import { MARCA_NOMBRE } from '../components/MarcaBiango'
import { t as i18nT } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// AQUÍ HABÍA EL NOMBRE DE UN CLIENTE ESCRITO A FUEGO ('Clínica Dental Esteve'), como respaldo por si
// el branding no había cargado. Es el mismo fallo que ya se corrigió en el hook useBranding: puesto
// de respaldo, el día que fallara la marca CUALQUIER otra clínica imprimiría un informe encabezado
// con el nombre de otra empresa — y este papel acaba en la asesoría. El respaldo ya no es un nombre:
// es NINGUNO. Sin nombre de clínica, la cabecera se queda sin esa línea (ver imprimirInformePDF) y
// el informe sigue siendo legible; con un nombre equivocado, no habría forma de darse cuenta.

// ── EL COLOR DE LA CLÍNICA EN UN DOCUMENTO QUE SE IMPRIME ────────────────────────────────────────
// El informe se pinta en una VENTANA NUEVA, que no hereda ni la hoja de estilos de la app ni sus
// variables: ahí dentro `var(--primary)` no existe y todo lo que no traiga un valor concreto sale
// negro. Así que el color se LEE del documento de la app y se inyecta como valor.
//
// EL INFORME ES UN PAPEL DE LA CLÍNICA, así que lleva SU color (el de «Datos y marca»). Lo deja en
// <html>, saneado, el proveedor de la marca (context/MarcaClinicaContext.jsx), en el atributo
// `data-color-documentos`, que no es una variable de CSS.
// Y no se lee `--primary`: es el azul de la aplicación y además cambia con el tema (en oscuro, el
// celeste, que sobre papel blanco se lee fatal). Sin color de clínica cae al azul de Biango, y si
// tampoco estuviera —la hoja de estilos no ha cargado— se queda con la tinta del texto.
//
// Y SE SANEA ANTES DE ENTRAR. Lo que sale de aquí se escribe DENTRO de una hoja de estilos de un
// documento nuevo: un valor con una llave o un `</style>` dejaría de ser un color y pasaría a ser
// código en una ventana que se abre sola. El color de la clínica ya se valida al guardarlo y otra
// vez al leerlo (utils/contraste, hexOk), pero esto es lo último antes de escribirlo, así que aquí
// solo pasa lo que TIENE FORMA de color. Cualquier otra cosa se trata como "no hay color".
const COLOR_OK = /^(#[0-9a-fA-F]{3,8}|rgba?\([\d\s.,%/]+\))$/
const tokenCSS = (nombre) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim()
    return COLOR_OK.test(v) ? v : ''
  } catch (_) { return '' }
}
const colorDeLaClinica = () => {
  try {
    const v = String(document.documentElement.getAttribute('data-color-documentos') || '').trim()
    return /^#[0-9a-fA-F]{6}$/.test(v) ? v : ''
  } catch (_) { return '' }
}
const colorMarca = () => colorDeLaClinica() || tokenCSS('--biango-azul') || 'currentColor'

// ── Utilidades de fecha (cliente). Formato local YYYY-MM-DD, sin toISOString (evita
//    desfase de zona horaria). El backend valida y filtra con ::date.
export const pad = n => String(n).padStart(2, '0')
export const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export function calcPreset(key) {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
  switch (key) {
    case 'hoy': return { desde: fmt(hoy), hasta: fmt(hoy) }
    case 'ayer': { const d = new Date(hoy); d.setDate(d.getDate() - 1); return { desde: fmt(d), hasta: fmt(d) } }
    case 'semana': { const dow = (hoy.getDay() + 6) % 7; const l = new Date(hoy); l.setDate(hoy.getDate() - dow); return { desde: fmt(l), hasta: fmt(hoy) } }
    case 'mes': return { desde: fmt(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: fmt(hoy) }
    case 'mes_pasado': return { desde: fmt(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)), hasta: fmt(new Date(hoy.getFullYear(), hoy.getMonth(), 0)) }
    case 'anio': return { desde: fmt(new Date(hoy.getFullYear(), 0, 1)), hasta: fmt(hoy) }
    case 'ult12m': { const d = new Date(hoy); d.setMonth(d.getMonth() - 12); return { desde: fmt(d), hasta: fmt(hoy) } }
    default: return null
  }
}
export const PRESETS = [
  ['hoy', 'Hoy'], ['ayer', 'Ayer'], ['semana', 'Esta semana'], ['mes', 'Este mes'],
  ['mes_pasado', 'Mes pasado'], ['anio', 'Este año'], ['ult12m', 'Últimos 12 meses'],
]
export const presetLabel = (p) => (PRESETS.find(([k]) => k === p) || [null, p])[1]
export const fechaBonita = ymd => ymd ? ymd.split('-').reverse().join('/') : ''
// Etiqueta "01/07/2026 – 31/07/2026" a partir de dos YYYY-MM-DD (nunca se construye con Date).
export const rangoTexto = (desde, hasta) => (desde && hasta) ? `${fechaBonita(desde)} – ${fechaBonita(hasta)}` : ''

// ── Exportar a CSV (lo abre Excel). BOM UTF-8 + separador ';' para España. Las columnas
//    numéricas con decimales se exportan con coma (Excel-ES) para que sean números reales.
const SEP = ';'
const quote = s => /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
const cell = (v, col) => {
  let s = v == null ? '' : String(v)
  if (col.tipo === 'num' && /^-?\d+\.\d+$/.test(s)) s = s.replace('.', ',')
  return quote(s)
}
const bloqueCSV = (columnas, filas) => [
  columnas.map(c => quote(c.label)).join(SEP),
  ...filas.map(f => columnas.map(c => cell(f[c.key], c)).join(SEP)),
].join('\r\n')

function bajarFichero(nombre, texto) {
  const blob = new Blob(['﻿' + texto], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = `${nombre}.csv`; document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(url)
}

export function descargarCSV(nombre, columnas, filas, rangoLabel = '') {
  // Mismo criterio de nombre que el PDF: si uno llevara la fecha y el otro no, el mismo informe se
  // guardaría con dos nombres distintos según el formato elegido.
  bajarFichero(nombreFicheroInforme(nombre, rangoLabel, new Date()), bloqueCSV(columnas, filas))
}

// CSV de un COMBINADO. Un CSV plano no admite varias tablas con columnas distintas, así que en vez de
// inventarse un formato o soltar N descargas de golpe (que el navegador bloquea a partir de la
// segunda), se apilan los bloques en UN fichero: título del bloque, su cabecera, sus filas y una
// línea en blanco de separación. Excel lo abre sin quejarse y no se pierde ningún dato; quien quiera
// una tabla limpia para dinamizar, genera ese informe suelto desde el constructor.
export function descargarCSVCombinado(nombre, bloques, rangoLabel) {
  const partes = []
  if (rangoLabel) partes.push(quote(`${nombre} — periodo común ${rangoLabel}`))
  bloques.forEach((b, i) => {
    const cab = b.rango_propio && b.rangoLabel ? `${i + 1}. ${b.nombre} (periodo propio: ${b.rangoLabel})` : `${i + 1}. ${b.nombre}`
    partes.push(quote(cab))
    if (b.error) partes.push(quote(b.error))
    else {
      partes.push(bloqueCSV(b.columnas || [], b.filas || []))
      const tot = Object.entries(b.totales || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')
      if (tot) partes.push(quote(tot))
    }
    partes.push('')   // línea en blanco entre tablas
  })
  bajarFichero(nombre, partes.join('\r\n'))
}

// ── Exportar a PDF: abre una ventana con HTML limpio (cabecera de la clínica) y lanza la
//    impresión del navegador (el usuario elige "Guardar como PDF"). Cero dependencias.
//
// bloques = [{ nombre?, columnas, filas, totales, rangoLabel?, rango_propio?, error? }]
// Con UN bloque sin nombre propio sale el documento clásico de un informe suelto (cabecera, totales
// y tabla). Con varios, cada bloque va en su sección: título numerado, su periodo si difiere del
// común, sus totales y su tabla.
const esc = v => String(v == null ? '' : v).replace(/[&<>]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]))
// B-156 · EL RESULTADO FINAL TAMBIÉN EN EL PAPEL. Este documento acaba en la asesoría y en las
// manos de quien lo pida: si en pantalla el resultado sale destacado y en el PDF vuelve a ser una
// etiqueta gris más entre ocho, es el mismo informe leído de dos formas distintas. `principal` es
// la clave que declara el servidor (`total_principal`); sin ella, el pie sale exactamente como
// salía antes.
const totalesHTML = (t, principal = null) => Object.entries(t || {}).map(([k, v]) =>
  `<span${k === principal ? ' class="pral"' : ''}><b>${esc(k)}:</b> ${esc(v)}</span>`).join('')
const tablaHTML = (columnas, filas) => {
  const th = columnas.map(c => `<th>${esc(c.label)}</th>`).join('')
  const rows = filas.map(f => `<tr>${columnas.map(c => `<td>${esc(f[c.key])}</td>`).join('')}</tr>`).join('')
  return `<table><thead><tr>${th}</tr></thead><tbody>${rows}</tbody></table>`
}

// EL NOMBRE DEL FICHERO. Al imprimir a PDF, el navegador propone como nombre el <title> del
// documento, así que ahí es donde se decide cómo se va a llamar el archivo en el disco de la
// clínica. Con solo el nombre del informe, sacar la hoja de cobro tres días seguidos daba tres
// "Hoja de cobro del día.pdf" y el segundo pisaba al primero (o quedaban (1), (2)... sin saber cuál
// es cuál). Se le añade el PERIODO al que se refiere y el día en que se emitió: dos informes iguales
// del mismo periodo sacados en días distintos se distinguen, y uno guardado hace meses se puede
// identificar sin abrirlo. Los caracteres que Windows no admite en un nombre de fichero se cambian
// por guiones aquí y no en el navegador, que los sustituiría a su manera.
const SIN_BARRAS = /[\/:*?"<>|]+/g
export function nombreFicheroInforme(nombre, rangoLabel, gen) {
  const dia = `${pad(gen.getDate())}-${pad(gen.getMonth() + 1)}-${gen.getFullYear()}`
  const partes = [String(nombre || 'Informe').trim(), String(rangoLabel || '').trim(), dia].filter(Boolean)
  return partes.join(' · ').replace(SIN_BARRAS, '-').slice(0, 180)
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA SALIDA DE LA VENTANA DEL DOCUMENTO  (B-038)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// EL FALLO. Los informes y la hoja de cobros no se enseñan dentro de la aplicación: se abre una
// ventana en blanco (`window.open('', '_blank')`), se le escribe el documento encima y se manda a
// imprimir sola. Ese documento no tenía NI UN control: ni un botón de cerrar, ni de volver, ni
// nada. Y el botón atrás tampoco servía, porque una ventana que se abre en blanco y se rellena con
// `document.write` tiene UNA sola entrada de historial: atrás no lleva a ningún sitio. En un
// ordenador se sale cerrando la pestaña; en un teléfono —donde la aplicación se usa a pantalla
// completa y no hay pestañas a la vista— la persona se quedaba ENCERRADA en la hoja de cobros del
// día, sin forma de volver a la aplicación. Un callejón sin salida.
//
// LA SALIDA, ENTONCES, SE PINTA. Una banda arriba del documento con «Cerrar» y con «Imprimir»
// (para volver a lanzar la impresión si la primera se canceló), y que NO sale en el papel. Un
// documento que se imprime con su propia barra de botones dentro no vale como documento.
//
// Y EL BOTÓN ATRÁS TAMBIÉN. Se añade a mano una entrada de historial: a partir de ahí sí existe un
// «atrás» al que volver, y al volver se cierra la ventana. Así el gesto de siempre —el atrás del
// navegador, el botón físico del móvil— hace lo que la persona espera.
//
// LO QUE NO SE PROMETE. `window.close()` cierra las ventanas que abrió un script, que es este caso,
// pero un navegador puede negarse. Si medio segundo después la ventana sigue abierta, se dice: se
// enseña el aviso de que hay que cerrarla a mano. Un botón que no hace nada y no lo cuenta es peor
// que no tener botón.
export const CLASE_SALIDA = 'bx-salida'
export const CSS_SALIDA = `
  .bx-salida { position: sticky; top: 0; z-index: 9; display: flex; align-items: center; gap: 10px;
    flex-wrap: wrap; padding: 9px 12px; margin: 0 0 14px; background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1; font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; }
  .bx-salida button { font: inherit; font-size: 13px; font-weight: 700; cursor: pointer;
    padding: 9px 16px; border-radius: 9px; border: 1px solid #94a3b8; background: #ffffff; color: #0f172a; }
  .bx-salida button.bx-p { background: #0f172a; border-color: #0f172a; color: #ffffff; }
  .bx-salida .bx-que { font-size: 12px; color: #475569; margin-left: auto; }
  .bx-aviso-cierre { display: none; margin: 0 0 14px; padding: 9px 12px; background: #fef3c7;
    border: 1px solid #fcd34d; border-radius: 8px; font-size: 12.5px; color: #92400e;
    font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; }
  @media print { .noprint, .bx-salida, .bx-aviso-cierre { display: none !important; } }`
// `que` = de qué documento se trata, para que la banda además oriente ("Hoja de cobros 03/09/2026").
export const htmlSalida = (que = '') => i18nT('utils.informesExport.div_class_bx_salida_noprint', '<div class="bx-salida noprint">\n  <button type="button" id="bx-cerrar" class="bx-p">Cerrar</button>\n  <button type="button" id="bx-imprimir">Imprimir o guardar en PDF</button>\n  {{x}}\n</div>\n<div class="bx-aviso-cierre noprint" id="bx-aviso-cierre">Este navegador no ha dejado cerrar la ventana. Ciérrala tú (la X de la pestaña) para volver a la aplicación.</div>', { x: que ? i18nT('utils.informesExport.span_class_bx_que_span', '<span class="bx-que">{{esc}}</span>', { esc: esc(que) }) : '' })
// `msPrint` = cuánto se espera antes de lanzar la impresión sola. Va aquí y no suelto en cada
// documento para que la banda y la impresión no puedan separarse: quitar el script para no imprimir
// se llevaría por delante la salida, y eso se ve en la primera prueba.
export const scriptSalida = (msPrint = 200) => `<script>
(function () {
  var cerrar = function () {
    try { window.close() } catch (e) {}
    setTimeout(function () {
      if (window.closed) return
      var a = document.getElementById('bx-aviso-cierre')
      if (a) a.style.display = 'block'
    }, 500)
  }
  var b = document.getElementById('bx-cerrar'); if (b) b.onclick = cerrar
  var p = document.getElementById('bx-imprimir'); if (p) p.onclick = function () { window.print() }
  try { history.pushState({ bx: 1 }, '', location.href) } catch (e) {}
  window.addEventListener('popstate', cerrar)
  setTimeout(function () { window.print() }, ${Number(msPrint) || 200})
})()
<\/script>`

// El pie de Biango va SIEMPRE (t91, 5-oct-2026: el dueño, «no debe poder quitarse»), como en los PDF del servidor
// (services/pdfTheme.selloActivo). Ya no hay parámetro que lo apague: una pantalla vieja que mande `sello: false` no
// cambia nada.
export function imprimirInformePDF({ nombre, clinica = '', rangoLabel = '', bloques = [] }) {
  const bl = bloques.filter(Boolean)
  if (!bl.length) return false
  const combinado = bl.length > 1
  const gen = new Date()
  const genStr = `${pad(gen.getDate())}/${pad(gen.getMonth() + 1)}/${gen.getFullYear()} ${pad(gen.getHours())}:${pad(gen.getMinutes())}`
  const totalFilas = bl.reduce((a, b) => a + (b.filas?.length || 0), 0)
  // EN UN COMBINADO NO SE SUMAN LAS FILAS. Cada bloque cuenta cosas distintas —una hoja de caja
  // cuenta COBROS y una lista de seguimiento cuenta PERSONAS—, así que '15 filas' juntando 10 cobros
  // y 5 pacientes no significa nada. Y es el único sitio donde la caja y el seguimiento se agregaban,
  // que es justo lo que se quiso evitar al separarlos en dos informes. Cada bloque ya lleva su propio
  // recuento en su cabecera.
  const meta = combinado
    ? `${rangoLabel ? 'Periodo común: ' + esc(rangoLabel) + ' · ' : ''}${bl.length} informes · Generado ${esc(genStr)}`
    : `${rangoLabel ? 'Periodo: ' + esc(rangoLabel) + ' · ' : ''}${totalFilas} fila(s) · Generado ${esc(genStr)}`
  const cuerpo = combinado
    ? bl.map((b, i) => `<section class="bloque">
  <h2>${i + 1}. ${esc(b.nombre || '')}</h2>
  ${b.rangoLabel ? `<div class="sub">${b.rango_propio ? 'Periodo propio' : 'Periodo'}: ${esc(b.rangoLabel)}</div>` : i18nT('utils.informesExport.div_class_sub_sin_periodo', '<div class="sub">Sin periodo (todos los datos)</div>')}
  ${b.error
    ? `<div class="aviso">${esc(b.error)}</div>`
    : ((b.filas?.length)
      ? `<div class="tot">${totalesHTML(b.totales, b.total_principal)}</div>${tablaHTML(b.columnas || [], b.filas)}`
      : i18nT('utils.informesExport.div_class_tot_div_div', '<div class="tot">{{totalesHTML}}</div><div class="aviso vacio">Sin resultados para estos filtros.</div>', { totalesHTML: totalesHTML(b.totales, b.total_principal) }))}
</section>`).join('\n')
    : `<div class="tot">${totalesHTML(bl[0].totales, bl[0].total_principal)}</div>
${tablaHTML(bl[0].columnas || [], bl[0].filas || [])}`
  // CSS extra SOLO para el documento combinado: un informe suelto sigue imprimiéndose con
  // exactamente las mismas reglas (y el mismo aspecto) que antes de existir los combinados.
  const cssCombinado = combinado ? `
  .bloque { margin-top: 22px; }
  .bloque:first-of-type { margin-top: 6px; }
  .bloque h2 { font-size: 13.5px; font-weight: 800; margin: 0 0 2px; color: var(--marca); border-bottom: 1px solid color-mix(in srgb, var(--marca) 25%, #ffffff); padding-bottom: 4px; break-after: avoid; page-break-after: avoid; }
  .bloque .sub { font-size: 11px; color: #666; margin-bottom: 6px; }
  .aviso { font-size: 11.5px; color: #92400e; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 6px; padding: 8px 10px; }
  .aviso.vacio { color: #666; background: #f4f4f5; border-color: #e4e4e7; }` : ''
  const win = window.open('', '_blank')
  if (!win) return false
  // ESTE PAPEL SALE DE LA CLÍNICA: el informe de caja acaba en la asesoría, y el de actividad en
  // manos de quien lo pida. Era el único documento que sale y no decía de quién es el software: su
  // pie ponía «Informe generado desde la app de gestión», que es justamente no decirlo. Lleva el
  // mismo sello que los PDF del servidor y con las mismas palabras.
  //
  // SIN EL DIBUJO DE LA MARCA, A PROPÓSITO. La ventana se manda imprimir sola a los 150 ms, y una
  // imagen que no ha terminado de cargar sale en blanco o mueve el pie a mitad de la impresión. Es
  // la misma decisión que toma el sello de los PDF cuando no encuentra el logotipo: la marca escrita
  // se lee siempre.
  const pieSello = `
  <div class="sello">${i18nT('utils.informesExport.sello_software_de_gestion', '{{esc}} · software de gestión para clínicas dentales', { esc: esc(MARCA_NOMBRE) })}</div>`
  const nombreClinica = String(clinica || '').trim()
  win.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(nombreFicheroInforme(nombre, rangoLabel, gen))}</title>
<style>
  /* El color de la clínica, ya resuelto: en esta ventana no existen los tokens de la app. */
  :root { --marca: ${colorMarca()}; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #111; margin: 24px; }
  header { border-bottom: 2px solid var(--marca); padding-bottom: 10px; margin-bottom: 14px; }
  header .clinica { font-size: 18px; font-weight: 800; color: var(--marca); }
  header .titulo { font-size: 15px; font-weight: 700; margin-top: 2px; }
  header .meta { font-size: 12px; color: #555; margin-top: 2px; }
  .tot { margin: 10px 0; font-size: 12px; }
  .tot span { display: inline-block; margin-right: 14px; background: #f1f5f9; border-radius: 6px; padding: 3px 8px; }
  /* El resultado final, destacado también en el papel (B-156): mismo criterio que la pantalla —el
     número que se busca al coger la hoja no puede ser idéntico a los otros siete. */
  .tot span.pral { font-size: 15px; font-weight: 800; padding: 5px 11px; background: color-mix(in srgb, var(--marca) 12%, #ffffff); border: 1px solid color-mix(in srgb, var(--marca) 45%, #ffffff); font-variant-numeric: tabular-nums; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #d4d4d8; padding: 4px 6px; text-align: left; vertical-align: top; }
  thead th { background: color-mix(in srgb, var(--marca) 8%, #ffffff); font-weight: 700; }
  tbody tr:nth-child(even) { background: #fafafa; }
  footer { margin-top: 14px; font-size: 10px; color: #888; }
  footer .sello { margin-top: 3px; color: #9aa3ae; }${cssCombinado}${CSS_SALIDA}
  @media print { @page { margin: 14mm; } thead { display: table-header-group; } }
</style></head><body>
${htmlSalida(nombre)}
<header>${nombreClinica ? `\n  <div class="clinica">${esc(nombreClinica)}</div>` : ''}
  <div class="titulo">${esc(nombre)}</div>
  <div class="meta">${meta}</div>
</header>
${cuerpo}
<footer>Informe emitido el ${esc(genStr)}${nombreClinica ? ' por ' + esc(nombreClinica) : ''}${pieSello}
</footer>
${scriptSalida(150)}
</body></html>`)
  win.document.close()
  return true
}

// Firma clásica de un informe suelto (la que usaba Informes.jsx): un solo bloque, documento idéntico.
export function imprimirPDF(nombre, columnas, filas, totales, rangoLabel, clinica = '') {
  return imprimirInformePDF({ nombre, clinica, rangoLabel, bloques: [{ columnas, filas, totales }] })
}
